/*
 * Auth context for the AquaStar customer web app.
 *
 * State: { user, status }
 *   - user comes from /auth/me on each refresh
 *   - status: 'unknown' | 'guest' | 'authed'
 *
 * Simpler than the admin console — there's no per-app role gate. Any
 * registered AquaStar user can use the app; ownership / follower
 * authorization happens per-pump server-side in each handler.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  type AuthMe,
  getMe,
  getToken,
  login as apiLogin,
  logout as apiLogout,
  setToken,
  SESSION_EXPIRED_EVENT,
} from './apiClient';

export type AuthStatus = 'unknown' | 'guest' | 'authed';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthMe | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /**
   * Cache an externally-issued access token (e.g. from
   * aquastarInviteAccept) and rehydrate the user state from /auth/me.
   * Used by the invite landing page after consuming the token.
   */
  adoptToken: (token: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [user, setUser] = useState<AuthMe | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setStatus('guest');
      return;
    }
    try {
      const me = await getMe();
      setUser(me);
      setStatus('authed');
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setStatus('guest');
        return;
      }
      setUser(null);
      setStatus('guest');
      setError(err instanceof Error ? err.message : 'Unable to verify session.');
    }
  }, []);

  // Boot — check existing session.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Session timeout: any 401 (token expired/invalid) dispatches this event from
  // the apiClient. Drop to 'guest' so RequireAuth redirects to /login; the Login
  // page reads the SESSION_EXPIRED flag and shows a friendly notice.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus('guest');
      setError(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        await apiLogin(email, password);
        await refresh();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
        setError(msg);
        throw err;
      }
    },
    [refresh],
  );

  const signOut = useCallback(() => {
    apiLogout();
    setUser(null);
    setStatus('guest');
    setError(null);
  }, []);

  const adoptToken = useCallback(
    async (token: string) => {
      setToken(token);
      await refresh();
    },
    [refresh],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, signIn, signOut, adoptToken, refresh }),
    [status, user, error, signIn, signOut, adoptToken, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
